<template>
  <div class="container mt-4" :key="selectedTab">
    <template>
      <TopNav
        :title="$t('GENERAL.LABEL.CONTRACTS')"
        :showOpenMainNav="true"
        :icon="['far', 'file-invoice']"
      >
        <NavItem
          icon="far fa-file-signature"
          :text="$t('CONTRACTS.LINKS.SEND_A_CONTRACT')"
          @click.native="sendContract"
        />
        <NavItem
          icon="far fa-plus"
          :text="$t('CONTRACTS.LINKS.NEW_TEMPLATE')"
          @click.native="addTemplate"
        />
      </TopNav>
      <div>
        <HMTabs v-model="selectedTab" class="mt-4 mb-3">
          <HMTab v-for="tab in tabs" :key="tab.name" :name="tab.name">
            {{ tab.title }}
          </HMTab>
        </HMTabs>
        <div class="row">
          <div class="col-12">
            <HMSearch v-model="form.search" />
          </div>
        </div>
        <div v-show="selectedTab === 'contracts'">
          <div class="row mt-3">
            <div class="col-12 col-md-4">
              <v-select
                :placeholder="$t('CONTRACTS.LABELS.ALL_STATUSES')"
                :options="contractStatuses"
                v-model="form.selectedStatus"
              />
            </div>
            <div class="col-12 col-md-4">
              <v-select
                :placeholder="$t('CONTRACTS.LABELS.ALL_PRODUCTS')"
                :options="allProducts"
                v-model="form.selectedProduct"
              />
            </div>
            <div class="col-12 col-md-4">
              <HMDatePicker v-model="form.date" class="flex-grow-1" />
            </div>
          </div>
          <HMPaginatedTable
            name="signed-contracts-table"
            api="contracts/getSignedContracts"
            :fields="signedContractsTableFields"
            :query="query"
            class="mt-3"
            ref="signed-contracts-table"
            :pageInQuery="true"
            :perPageFixed="10"
          >
            <template v-slot:full_name="{ data }">
              <router-link
                v-if="data.student"
                :to="`/customers/${data.student.id}`"
                class="d-block"
              >
                {{ data.full_name }}
              </router-link>
              <span v-else>
                {{ data.full_name }}
              </span>
            </template>
            <template v-slot:email="{ data }">
              {{ data.email }}
            </template>
            <template v-slot:product="{ data }">
              {{ data.product ? data.product.product_name : '-' }}
            </template>
            <template v-slot:contract_title="{ data }">
              {{ data.contract.title }}
            </template>
            <template v-slot:status="{ data }">
              <div
                class="d-inline-flex justify-content-start align-items-center flex-nowrap"
              >
                <font-awesome-icon
                  :icon="['fas', 'circle']"
                  :class="`dot dot--${!!data.document ? 'signed' : 'pending'}`"
                />
                <span class="c-charcoal">{{
                  !!data.document
                    ? $t('CONTRACTS.LABELS.SIGNED')
                    : $t('CONTRACTS.LABELS.PENDING')
                }}</span>
              </div>
            </template>
            <template v-slot:signed="{ data }">
              {{ $formatDate(data.signed, 'date-time', '-') }}
            </template>
            <template v-slot:created="{ data }">
              {{ $formatDate(data.created, 'date-time', '-') }}
            </template>
            <template slot="edit" slot-scope="{ data }">
              <HMIconButton
                :icon="['far', 'ellipsis-v']"
                :id="`popover-${data.id}`"
              />
              <HMPopover :target="`popover-${data.id}`" boundary-padding="-20">
                <HMPopoverMenuItem
                  v-if="!data.document"
                  @click="resendSignedContract(data.id)"
                >
                  {{ $t('CONTRACTS.LINKS.RESEND') }}
                </HMPopoverMenuItem>
                <HMPopoverMenuItem
                  v-if="data.document"
                  @click="viewSignedContract(data)"
                >
                  {{ $t('CONTRACTS.LINKS.VIEW') }}
                </HMPopoverMenuItem>
                <HMPopoverMenuItem
                  v-if="!data.document"
                  @click="uploadSignedContractDocument(data)"
                >
                  {{ $t('CONTRACTS.LINKS.UPLOAD_SIGNED_CONTRACT') }}
                </HMPopoverMenuItem>
                <HMPopoverMenuItem
                  v-if="!data.document"
                  @click="showSignedContractDeleteModal(data)"
                >
                  <span
                    class="d-flex justify-content-between align-items-center"
                  >
                    {{ $t('CONTRACTS.LINKS.DELETE') }}
                    <i class="far fa-trash-alt c-coral ml-4"></i>
                  </span>
                </HMPopoverMenuItem>
              </HMPopover>
            </template>
            <template v-slot:no-data>
              <HMEmpty
                image="jands-webinar-bw"
                :title="$t('CONTRACTS.EMPTY.TITLE')"
                :description="$t('CONTRACTS.EMPTY.DESCRIPTION')"
              />
            </template>
          </HMPaginatedTable>
        </div>
        <div v-show="selectedTab === 'templates'">
          <HMPaginatedTable
            name="template-contracts-table"
            api="contracts/getContracts"
            :fields="templatesTableFields"
            :query="query"
            class="mt-3"
            ref="template-contracts-table"
            :pageInQuery="true"
            :perPageFixed="10"
          >
            <template v-slot:title="{ data }">
              <router-link
                :to="{
                  name: 'Edit contract',
                  params: { id: data.id },
                }"
              >
                {{ data.title }}
              </router-link>
            </template>
            <template v-slot:num_signed="{ data }">
              {{ data.num_signed || '-' }}
            </template>
            <template v-slot:num_unsigned="{ data }">
              {{ data.num_unsigned || '-' }}
            </template>
            <template v-slot:updated="{ data }">
              {{ $formatDate(data.updated, 'date-time', '-') }}
            </template>
            <!--            <template slot="edit" slot-scope="{ data }">-->
            <!--              <HMIconButton-->
            <!--                  :icon="['far', 'ellipsis-v']"-->
            <!--                  :id="`popover-${data.id}`"-->
            <!--              />-->
            <!--              <HMPopover :target="`popover-${data.id}`" boundary-padding="-20">-->
            <!--              </HMPopover>-->
            <!--            </template>-->
            <template v-slot:no-data>
              <HMEmpty
                image="jands-webinar-bw"
                :title="$t('CONTRACTS.EMPTY.TEMPLATES_TITLE')"
                :description="$t('CONTRACTS.EMPTY.TEMPLATES_DESCRIPTION')"
              />
            </template>
          </HMPaginatedTable>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { get } from 'lodash';
import TopNav from '@/web/components/navigation/TopNav.vue';
import NavItem from '@/web/components/navigation/NavItem.vue';
import { mapGetters } from 'vuex';
import NewContractTemplateModal from '../../../components/contracts/NewContractTemplateModal';
import SendContractModal from '../../../components/contracts/SendContractModal';
import { notify } from '../../../utils/notify';
import { getStudentViewURL } from '../../../router/custom-domain-utils';
import HMDialog from '../../../components/common/HMDialog';
import { errorToMessage } from '../../../../core/utils/errors';
import UploadSignedContractModal from '../../../components/contracts/UploadSignedContractModal';

export default {
  name: 'Contracts',
  components: {
    TopNav,
    NavItem,
  },
  computed: {
    ...mapGetters({
      allTeacherProducts: 'product/allTeacherProducts',
      teacherStudioUrl: 'user/teacherStudioUrl',
    }),
    query() {
      if (this.selectedTab === 'contracts') {
        return {
          q: this.form.search,
          status: get(this.form.selectedStatus, 'value'),
          product: get(this.form.selectedProduct, 'value'),
          startDate: this.form.date?.startDate,
          endDate: this.form.date?.endDate,
        };
      } else {
        return {
          q: this.form.search,
        };
      }
    },
    contractStatuses() {
      return [
        {
          label: this.$t('CONTRACTS.LABELS.PENDING'),
          value: 'pending',
        },
        {
          label: this.$t('CONTRACTS.LABELS.SIGNED'),
          value: 'signed',
        },
      ];
    },
    allProducts() {
      return (this.allTeacherProducts ?? []).map(product => ({
        label: product.product_name,
        value: product.id,
      }));
    },
    tabs() {
      return [
        {
          name: 'contracts',
          title: this.$t('CONTRACTS.LABELS.CONTRACTS'),
        },
        {
          name: 'templates',
          title: this.$t('CONTRACTS.LABELS.TEMPLATES'),
        },
      ];
    },
    signedContractsTableFields() {
      return [
        {
          name: 'full_name',
          title: this.$t('CONTRACTS.TABLE.FULL_NAME'),
        },
        {
          name: 'email',
          title: this.$t('CONTRACTS.TABLE.EMAIL'),
        },
        {
          name: 'product',
          title: this.$t('CONTRACTS.TABLE.PRODUCT'),
        },
        {
          name: 'contract_title',
          title: this.$t('CONTRACTS.TABLE.CONTRACT_TITLE'),
        },
        {
          name: 'status',
          title: this.$t('CONTRACTS.TABLE.STATUS'),
        },
        {
          name: 'signed',
          title: this.$t('CONTRACTS.TABLE.SIGNED_ON'),
        },
        {
          name: 'created',
          title: this.$t('CONTRACTS.TABLE.CREATED'),
        },
        {
          name: 'edit',
          shrink: true,
          cssClass: 'p-0',
          disableClick: true,
        },
      ];
    },
    templatesTableFields() {
      return [
        {
          name: 'title',
          title: this.$t('CONTRACTS.TABLE.CONTRACT_TITLE'),
        },
        {
          name: 'num_signed',
          title: this.$t('CONTRACTS.TABLE.NUM_SIGNED'),
        },
        {
          name: 'num_unsigned',
          title: this.$t('CONTRACTS.TABLE.NUM_PENDING'),
        },
        {
          name: 'updated',
          title: this.$t('CONTRACTS.TABLE.LAST_UPDATED'),
        },
        {
          name: 'edit',
          shrink: true,
          cssClass: 'p-0',
          disableClick: true,
        },
      ];
    },
  },
  data() {
    return {
      isLoading: true,
      selectedTab: 'contracts',
      form: {
        search: '',
        selectedStatus: '',
        selectedProduct: '',
        date: {},
      },
    };
  },
  methods: {
    async sendContract() {
      this.$modal.show(
        SendContractModal,
        {},
        {},
        {
          onContractSent: signedContract => {
            console.log('Signed contract created', signedContract);
            this.refreshAndOpenSignedContractsTable();
          },
        }
      );
    },
    addTemplate() {
      this.$modal.show(
        NewContractTemplateModal,
        {},
        {
          clickToClose: false,
        },
        {
          onContractTemplateCreated: contractTemplate => {
            console.log('Contract template created', contractTemplate);
            this.refreshAndOpenContractTemplatesTable();
          },
        }
      );
    },
    refreshAndOpenContractTemplatesTable() {
      this.selectedTab = 'templates';
      this.$refs['template-contracts-table'].refresh();
    },
    refreshAndOpenSignedContractsTable() {
      this.selectedTab = 'contracts';
      this.$refs['signed-contracts-table'].refresh();
    },
    viewSignedContract(signedContract) {
      window.open(signedContract.document.storage_url);
    },
    copySignedContractLink(signedContract) {
      const route = this.$router.resolve({
        name: 'studio-sign-contract',
        params: {
          studio_url: this.teacherStudioUrl,
          unique_id: signedContract?.unique_id,
        },
      });
      console.log(route);
      const routeUrl = route.href.replaceAll(`${this.teacherStudioUrl}/`, '');
      const url = getStudentViewURL(this.teacherStudioUrl, routeUrl);
      console.log(url);
      this.$copyText(url).then(() => {
        notify(this.$t('NOTIFICATIONS.TEXT.COPIED'), url);
      });
    },
    async showSignedContractDeleteModal(signedContract) {
      if (signedContract.document) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Contract can not be deleted',
          text: 'Only pending contracts can be deleted',
        });
        return;
      }
      this.$modal.show(HMDialog, {
        title: this.$t('CONTRACTS.TITLES.DELETE_CONTRACT'),
        text: this.$t('CONTRACTS.TEXT.CONFIRM_DELETE_CONTRACT'),
        confirmButton: this.$t('GENERAL.BUTTON.DELETE'),
        confirm: async () => {
          await this.deleteSignedContract(signedContract);
        },
      });
    },
    async deleteSignedContract(signedContract) {
      try {
        await this.$store.dispatch(
          'contracts/deleteSignedContract',
          signedContract.id
        );
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t(
            'CONTRACTS.NOTIFICATIONS.CONTRACT_SUCCESSFULLY_DELETED'
          ),
        });
        this.refreshAndOpenSignedContractsTable();
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to delete contract',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
    uploadSignedContractDocument(signedContract) {
      this.$modal.show(
        UploadSignedContractModal,
        {},
        {},
        {
          onFileUploaded: async file => {
            console.log('File uploaded', file);
            signedContract.document = file.id;
            try {
              await this.$store.dispatch(
                'contracts/uploadSignedContract',
                signedContract
              );
              this.$notify({
                group: 'app',
                type: 'success',
                title: this.$t(
                  'CONTRACTS.NOTIFICATIONS.SIGNED_CONTRACT_SUCCESSFULLY_UPLOADED'
                ),
              });
              this.refreshAndOpenSignedContractsTable();
            } catch (error) {
              this.$notify({
                group: 'app',
                type: 'error',
                title: 'Failed to upload signed contract',
                text: errorToMessage(
                  error.response,
                  `Request failed: ${error}`
                ),
                duration: -1, // stay ON
              });
            }
          },
        }
      );
    },
    async resendSignedContract(contractId) {
      try {
        await this.$store.dispatch(
          'contracts/resendSignedContract',
          contractId
        );
        this.$notify({
          group: 'app',
          type: 'success',
          title: this.$t(
            'CONTRACTS.NOTIFICATIONS.SIGNED_CONTRACT_SUCCESSFULLY_RESENT'
          ),
        });
        this.refreshAndOpenSignedContractsTable();
      } catch (error) {
        this.$notify({
          group: 'app',
          type: 'error',
          title: 'Failed to resend the contract',
          text: errorToMessage(error.response, `Request failed: ${error}`),
          duration: -1, // stay ON
        });
      }
    },
  },
  async mounted() {
    this.$store.dispatch('product/getTeacherProducts');
  },
};
</script>

<style lang="scss" scoped>
@import '@/web/styles/components-variables.scss';
.status {
  padding: 4px 8px;
  border-radius: $border-radius;
  &--draft {
    background-color: lighten($coral, 29%);
    color: $coral;
  }
  &--sent {
    background-color: lighten($light-green, 15%);
    color: $forest;
  }
  &--scheduled {
    background-color: lighten($brown, 25%);
    color: $white;
  }
}

.dot {
  margin-right: 6px;
  font-size: 0.5rem;
  &--signed {
    color: $light-green;
  }
  &--pending {
    color: $mustard;
  }
}
</style>
